import * as React from "react";
import { Form, Formik } from "formik";
import { Plus } from "lucide-react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import * as Yup from "yup";
import * as Tooltip from "@radix-ui/react-tooltip";
import { Box, Flex, Text, Button, Badge } from "@radix-ui/themes";
import SideDrawer from "@/components/ui/SideDrawer";
import { entityService } from "@/services/breeze/EntityService";
import { fieldService } from "@/services/breeze/FieldService";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import { useSearchParams } from "react-router-dom";
import {
  CreateField,
  StandardFieldDatabaseType,
  StandardFieldDisplayType,
  UpdateField,
  Field,
} from "@/types/breeze/Field";
import CreateUpdateFieldForm from "@/components/data-model/CreateUpdateFieldForm";
import { DraggableFieldList } from "@/components/data-model/DraggableFieldList";
import { buildFieldValidationSchema } from "@/components/data-model/fieldValidationBuilder";

// Initial validation schema - will be updated dynamically by the form
const initialValidationSchema = buildFieldValidationSchema(
  StandardFieldDisplayType.INPUT,
  StandardFieldDatabaseType.VARCHAR,
  false,
);

export default function DataModelEditor() {
  const [isFieldDrawerOpen, setIsFieldDrawerOpen] = React.useState(false);
  const [isUpdateFieldDrawerOpen, setIsUpdateFieldDrawerOpen] =
    React.useState(false);
  const [selectedField, setSelectedField] = React.useState<Field | null>(null);
  const [optimisticFields, setOptimisticFields] = React.useState<
    Field[] | null
  >(null);
  const [validationSchema, setValidationSchema] = React.useState<
    Yup.ObjectSchema<any>
  >(initialValidationSchema);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const entityName = searchParams.get("entityName");

  const handleValidationSchemaChange = React.useCallback(
    (newSchema: Yup.ObjectSchema<any>) => {
      setValidationSchema(newSchema);
    },
    [],
  );

  const {
    data: entity,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["entity", entityName, true],
    queryFn: () => (entityName ? entityService.getByName(entityName, true) : null),
    enabled: !!entityName,
  });

  // Use optimistic fields if available, otherwise use server fields
  const displayFields = optimisticFields || entity?.fields || [];

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["entity", entityName] });
    queryClient.invalidateQueries({ queryKey: ["entities"] });
  };

  const createField = useMutation({
    mutationFn: (data: CreateField) =>
      entityName
        ? fieldService.createField(entityName, data)
        : Promise.reject(),
    onSuccess: () => {
      invalidateQueries();
      setIsFieldDrawerOpen(false);
      // Clear optimistic state when real data comes in
      setOptimisticFields(null);
    },
  });

  const updateField = useMutation({
    mutationFn: (data: UpdateField) =>
      entityName
        ? fieldService.updateField(entityName, selectedField!.id, data)
        : Promise.reject(),
    onSuccess: () => {
      invalidateQueries();
      setIsUpdateFieldDrawerOpen(false);
      setSelectedField(null);
      // Clear optimistic state when real data comes in
      setOptimisticFields(null);
    },
  });

  const updateFieldOrder = useMutation({
    mutationFn: async (reorderedFields: Field[]) => {
      if (!entityName) return Promise.reject();

      return fieldService.reorderFields(
        entityName,
        reorderedFields.map((f) => ({
          field_id: f.id,
          sort_order: f.sort_order,
        })),
      );
    },
    onMutate: async (reorderedFields) => {
      await queryClient.cancelQueries({ queryKey: ["entity", entityName] });
      setOptimisticFields(reorderedFields);
    },
    onError: () => {
      setOptimisticFields(null);
    },
    onSettled: () => {
      invalidateQueries();
    },
  });

  const deleteField = useMutation({
    mutationFn: (fieldId: number) =>
      entityName
        ? fieldService.deleteField(entityName, fieldId)
        : Promise.reject(),
    onSuccess: () => {
      invalidateQueries();
      setOptimisticFields(null);
    },
  });

  const handleDeleteField = async (field: Field) => {
    const confirmed = await confirm({
      title: "Delete Field",
      message: `Are you sure you want to delete the field "${field.name}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
    });

    if (confirmed) {
      deleteField.mutate(field.id);
    }
  };

  const handleFieldReorder = (reorderedFields: Field[]) => {
    // Immediately update optimistic state for smooth UI
    setOptimisticFields(reorderedFields);
    // Then update the server
    updateFieldOrder.mutate(reorderedFields);
  };

  const handleEditField = (field: Field) => {
    setSelectedField(field);
    setIsUpdateFieldDrawerOpen(true);
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Clear optimistic state when fresh data arrives from server (after mutation settles)
  React.useEffect(() => {
    if (
      entity?.fields &&
      !updateFieldOrder.isPending &&
      !updateFieldOrder.isSuccess
    ) {
      setOptimisticFields(null);
    }
  }, [entity?.fields, updateFieldOrder.isPending, updateFieldOrder.isSuccess]);

  // Memoized initial values for update form to map database types safely
  const initialUpdateValues = React.useMemo(() => {
    if (!selectedField) return null;
    const copy = { ...selectedField };
    if (
      copy.database_type === StandardFieldDatabaseType.JSON &&
      copy.display_type === StandardFieldDisplayType.GEO
    ) {
      copy.database_type = "geo" as any;
    }
    return copy;
  }, [selectedField]);

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  if (error) {
    return <Box>Error loading entity</Box>;
  }

  if (!entity) {
    return <Box>Entity not found</Box>;
  }

  return (
    <Tooltip.Provider>
      <Box
        style={{
          height: "100%",
          // Ensure no scroll containers that could interfere with drag and drop
          overflow: "visible",
        }}
      >
        <Box
          pl="4"
          pr="0"
          style={{
            // Ensure this container doesn't scroll
            overflow: "visible",
          }}
        >
          <Flex justify="between" align="start" mb="4">
            <Flex direction="column" flexGrow={"1"}>
              <Flex align="center" gap="3">
                <Text size="4" weight="bold">
                  {entity.name}
                </Text>
                <Button
                  onClick={() => setIsFieldDrawerOpen(true)}
                  size="1"
                  style={{ marginLeft: "auto" }}
                  disabled={entity.locked}
                >
                  <Plus style={{ width: "16px", height: "16px" }} />
                  Add Field
                </Button>
              </Flex>
              <Box mt="1">
                <Text size="2" color="gray">
                  Created {formatDateTime(entity.created_at)}
                </Text>
                <br />
                <Text size="2" color="gray">
                  Updated {formatDateTime(entity.updated_at)}
                </Text>
                {entity.enable_rls && (
                  <Box mt="2">
                    <Badge variant="soft">Row-Level Security Enabled</Badge>
                  </Box>
                )}
              </Box>
            </Flex>
          </Flex>

          <Box>
            <Flex justify="between" align="center" mb="2">
              <Text size="4" weight="bold">
                Fields
              </Text>
            </Flex>

            <Box
              style={{
                // Ensure the container for the draggable list doesn't create scroll issues
                overflow: "visible",
              }}
            >
              <DraggableFieldList
                entity={entity}
                fields={displayFields}
                onReorder={handleFieldReorder}
                onEdit={handleEditField}
                onDelete={handleDeleteField}
              />
            </Box>
          </Box>
        </Box>

        <SideDrawer
          isOpen={isFieldDrawerOpen}
          onClose={() => setIsFieldDrawerOpen(false)}
          title="Create New Field"
          width="max-w-2xl"
          saveButton={{
            formId: "create-field-form",
            label: "Create Field",
            loading: createField.isPending,
          }}
        >
          <div>
            <Formik<CreateField>
              initialValues={{
                name: "",
                label: "",
                database_type: StandardFieldDatabaseType.VARCHAR,
                display_type: StandardFieldDisplayType.INPUT,
                description: undefined,
                default_value: undefined,
                is_required: false,
                is_name_field: false,
                is_unique: false,
                is_immutable: false,
                is_searchable: false,
                publicly_searchable: false,
                is_indexed: false,
                relationship: null,
                options: {
                  input: {
                    addon_before: undefined,
                    addon_after: undefined,
                    placeholder: undefined,
                    masked: false,
                  },
                  select: null,
                  textarea: null,
                  rich_text: null,
                  file: null,
                },
                width: 100,
              }}
              onSubmit={(values: CreateField) => {
                // The backend will automatically assign the next sort_order for new fields
                const payload = { ...values };
                if (
                  payload.database_type ===
                    (StandardFieldDatabaseType.GEO as any) ||
                  payload.database_type === "geo"
                ) {
                  payload.database_type = StandardFieldDatabaseType.JSON;
                  payload.display_type = StandardFieldDisplayType.GEO;
                }
                createField.mutate(payload);
              }}
              validationSchema={validationSchema}
            >
              {() => (
                <Form id="create-field-form" className="space-y-4">
                  <CreateUpdateFieldForm
                    entityName={entityName ?? ""}
                    submitting={createField.isPending}
                    isUpdate={false}
                    onValidationSchemaChange={handleValidationSchemaChange}
                  />
                </Form>
              )}
            </Formik>
          </div>
        </SideDrawer>

        <SideDrawer
          isOpen={isUpdateFieldDrawerOpen}
          onClose={() => setIsUpdateFieldDrawerOpen(false)}
          title="Update Field"
          width="max-w-2xl"
          saveButton={{
            formId: "update-field-form",
            label: "Update Field",
            loading: updateField.isPending,
          }}
        >
          <div>
            {selectedField && initialUpdateValues && (
              <Formik<UpdateField>
                initialValues={initialUpdateValues}
                onSubmit={(values: UpdateField) => {
                  const payload = { ...values };
                  if (
                    payload.database_type ===
                      (StandardFieldDatabaseType.GEO as any) ||
                    payload.database_type === "geo"
                  ) {
                    payload.database_type = StandardFieldDatabaseType.JSON;
                    payload.display_type = StandardFieldDisplayType.GEO;
                  }
                  updateField.mutate(payload);
                }}
                validationSchema={validationSchema}
              >
                <Form id="update-field-form" className="space-y-4">
                  <CreateUpdateFieldForm
                    entityName={entityName ?? ""}
                    submitting={updateField.isPending}
                    isUpdate={true}
                    onValidationSchemaChange={handleValidationSchemaChange}
                  />
                </Form>
              </Formik>
            )}
          </div>
        </SideDrawer>
      </Box>
    </Tooltip.Provider>
  );
}
